/**
 * AuthContext.js — authentication and tenant state.
 *
 * DEFECTS IN v1 THAT THIS FIXES
 *
 *  1. The onIdTokenChanged subscription was never unsubscribed.
 *  2. getBusinessData() opened a document listener and returned its cleanup to
 *     a caller that discarded it — and it called the three settings loaders
 *     from INSIDE the business snapshot callback, so every write to the
 *     business document opened three more permanent listeners. A session that
 *     edited the profile twenty times ended with sixty live listeners.
 *  3. `if (doc.exists)` treated a method as a property, so it was always true;
 *     a missing business document became `{ id, ...undefined }`.
 *  4. Loading was gated on `setTimeout(..., 1500)` rather than on the token
 *     actually resolving, so downstream code saw null settings and threw, and
 *     a slow token redirected a valid user back to the landing page.
 *  5. The context value object was rebuilt every render with no useMemo, so
 *     every consumer subtree re-rendered on any state change.
 *  6. Custom claims were never force-refreshed after being set, so a newly
 *     provisioned business bounced back to /setup-profile. v1 papered over
 *     this with a hardcoded five-second setTimeout.
 *  7. There was no sign-out at all — `const logout = () => { }`.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { onIdTokenChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext(null);

const SETTINGS_KEYS = ['general', 'sales', 'purchase', 'reminders'];

export function AuthContextProvider({ children }) {
  const [user, setUser] = useState(null);
  const [claims, setClaims] = useState(null);
  /** 'initialising' | 'signed-out' | 'needs-setup' | 'ready' */
  const [status, setStatus] = useState('initialising');

  const [business, setBusiness] = useState(null);
  const [businessError, setBusinessError] = useState(null);
  const [settings, setSettings] = useState({});
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const businessId = claims?.businessID || null;

  /**
   * THE ROLE, EXPOSED. It was not, and that was a bug with no symptom.
   *
   * The tenant id has always been lifted out of the claims and handed over as
   * `businessId`; the role never was, so `useAuth()` returned `claims` and
   * nothing else. Six screens wrote
   *
   *     const { businessId, role } = useAuth();
   *
   * which reads perfectly and yields `undefined` — `can(undefined, anything)`
   * is false, so every capability check on those screens failed for EVERY
   * user, an owner exactly as firmly as a viewer. The Chart of Accounts, the
   * Day Book, the accounting voucher list, its detail and its editor, and the
   * accounting settings section all rendered with their controls disabled and
   * no explanation, because a permission check that returns false is
   * indistinguishable from a permission that was never granted.
   *
   * It survived because of how it fails. A crash is reported in a minute; a
   * greyed-out button is worked around for a year.
   *
   * The fix belongs HERE and not in the six call sites. Both spellings now
   * work, so neither the screens that read `claims?.role` nor the ones that
   * read `role` can be wrong — and the seventh screen that reaches for it will
   * be right without anybody having to know this story.
   *
   * Empty string rather than null, because that is what `can()` and
   * `routeRefusal()` compare against, and it keeps every call site free of a
   * `|| ''` that is easy to leave out.
   */
  const role = claims?.role || '';

  /* --- auth subscription ------------------------------------------ */

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setClaims(null);
        setBusiness(null);
        setSettings({});
        setStatus('signed-out');
        return;
      }

      try {
        const tokenResult = await firebaseUser.getIdTokenResult();
        const nextClaims = tokenResult?.claims || {};
        setUser(firebaseUser);
        setClaims(nextClaims);
        // Status is driven by the token, not by a timer.
        setStatus(nextClaims.isBusiness && nextClaims.businessID ? 'ready' : 'needs-setup');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[auth] could not read the ID token', error);
        setUser(firebaseUser);
        setClaims({});
        setStatus('needs-setup');
      }
    });

    // v1 never called this.
    return () => unsubscribe();
  }, []);

  /* --- business document ------------------------------------------ */

  useEffect(() => {
    if (!businessId) {
      setBusiness(null);
      setBusinessError(null);
      return undefined;
    }

    let active = true;
    const unsubscribe = onSnapshot(
      doc(db, 'Businesses', businessId),
      (snapshot) => {
        if (!active) return;
        // exists() is a method. v1 read it as a property.
        if (snapshot.exists()) {
          setBusiness({ id: snapshot.id, ...snapshot.data() });
          setBusinessError(null);
        } else {
          setBusiness(null);
          setBusinessError(new Error('The business profile could not be found.'));
        }
      },
      (error) => {
        if (!active) return;
        // eslint-disable-next-line no-console
        console.error('[auth] business subscription failed', error);
        setBusinessError(error);
      }
    );

    return () => {
      active = false;
      unsubscribe();
    };
  }, [businessId]);

  /* --- settings ----------------------------------------------------
   * Subscribed as siblings of the business listener, NOT nested inside its
   * callback. This is the fix for the compounding-listener leak.
   * ---------------------------------------------------------------- */

  /**
   * WHICH keys have reported, not HOW MANY reports have arrived.
   *
   * This used to be a counter incremented on every snapshot. Firestore delivers
   * a cached snapshot and then a server snapshot for the same document, and it
   * echoes local writes straight back — so `general` alone could fire four
   * times and flip `settingsLoaded` to true while `sales` had not arrived at
   * all. Anything that hydrates once off `settingsLoaded` then hydrated from an
   * empty `settings` object and showed defaults: that is why a saved Serial
   * Padding came back as 4, and why saving again wrote the default over it.
   */
  const loadedKeys = useRef(new Set());

  useEffect(() => {
    if (!businessId) {
      setSettings({});
      setSettingsLoaded(false);
      return undefined;
    }

    let active = true;
    loadedKeys.current = new Set();
    setSettingsLoaded(false);

    const markLoaded = (key) => {
      loadedKeys.current.add(key);
      if (loadedKeys.current.size >= SETTINGS_KEYS.length) setSettingsLoaded(true);
    };

    const unsubscribers = SETTINGS_KEYS.map((key) =>
      onSnapshot(
        doc(db, 'Businesses', businessId, 'Settings', key),
        (snapshot) => {
          if (!active) return;
          setSettings((prev) => ({
            ...prev,
            [key]: snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null,
          }));
          markLoaded(key);
        },
        (error) => {
          if (!active) return;
          // eslint-disable-next-line no-console
          console.error(`[auth] settings/${key} subscription failed`, error);
          setSettings((prev) => ({ ...prev, [key]: null }));
          markLoaded(key);
        }
      )
    );

    return () => {
      active = false;
      unsubscribers.forEach((u) => u());
    };
  }, [businessId]);

  /* --- actions ----------------------------------------------------- */

  /**
   * Force a token refresh. Call this after the server changes custom claims,
   * so the client sees `isBusiness` immediately instead of waiting up to an
   * hour (v1's workaround was a five-second setTimeout and a hope).
   */
  const refreshClaims = useCallback(async () => {
    if (!auth.currentUser) return null;
    const tokenResult = await auth.currentUser.getIdTokenResult(true);
    const nextClaims = tokenResult?.claims || {};
    setClaims(nextClaims);
    setStatus(nextClaims.isBusiness && nextClaims.businessID ? 'ready' : 'needs-setup');
    return nextClaims;
  }, []);

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth);
    setUser(null);
    setClaims(null);
    setBusiness(null);
    setSettings({});
    setStatus('signed-out');
  }, []);

  /* --- context value ----------------------------------------------- */

  const value = useMemo(
    () => ({
      user,
      claims,
      /** Lifted out of the claims, like `businessId`. See the note above. */
      role,
      status,
      isLoading: status === 'initialising',
      isSignedIn: !!user,
      businessId,
      business,
      businessError,
      /** The supplier state code, used for every tax determination. */
      supplierStateCode: business?.stateCode || null,
      settings,
      settingsLoaded,
      salesSettings: settings.sales || null,
      purchaseSettings: settings.purchase || null,
      reminderSettings: settings.reminders || null,
      generalSettings: settings.general || null,
      refreshClaims,
      signOut,
    }),
    [
      user,
      claims,
      role,
      status,
      businessId,
      business,
      businessError,
      settings,
      settingsLoaded,
      refreshClaims,
      signOut,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside an AuthContextProvider.');
  }
  return context;
}


